import { TIERS, getFamiliesForModule, getTierRangeForGroup, type ModuleId } from '../content';
import { GroupId } from '../config/systemConfig';
import { warn } from '../utils/logger';

/**
 * Placement Engine — Date-based phase resolution and starting tier logic.
 *
 * NOTE: The original diagnostic placement *test* concept was removed (B/M/E
 * phase is now derived from the calendar date). The functions in this file
 * (resolvePhaseByDate, getAutoStartingTier, getAutoPhaseLabel) are actively
 * used by the adaptive engine's cold-start logic.
 *
 * RC pass (mei 2026): the legacy `getPlacementKey` helper and the unused
 * `PlacementResult` import have been removed — `savePlacementResult` and the
 * `placementResults` AppState field were already stripped in
 * PR_SELECTEDGROUP_CLEANUP, leaving no callers.
 */

// ─── §4.4 Date-Based Placement Strategy ───────────────────────────────────────
// Merged from dateBasedPlacement.ts (stabilization §4.4/22)
// Provides automatic starting tier based on school year phase.

export type SchoolYearPhase = 'B' | 'M' | 'E';

/**
 * Resolve the school year phase based on the current date.
 *
 * Logic:
 * - Aug 1 (Month 7) – Oct 31 (Month 9) = 'B' (Begin)
 * - Nov 1 (Month 10) – Mar 31 (Month 2) = 'M' (Midden)
 * - Apr 1 (Month 3) – Jul 31 (Month 6) = 'E' (Eind)
 */
export function resolvePhaseByDate(date: Date = new Date()): SchoolYearPhase {
  const month = date.getMonth(); // 0 = Jan, 11 = Dec

  // Aug (7) to Oct (9) -> B
  if (month >= 7 && month <= 9) return 'B';

  // Nov (10) to Dec (11) OR Jan (0) to Mar (2) -> M
  if (month >= 10 || month <= 2) return 'M';

  // Apr (3) to Jul (6) -> E
  return 'E';
}

/**
 * The distinct tiers (within a group's band) that actually have authored
 * families — and therefore questions — for a given module.
 *
 * This mirrors the eligibility filter used by the adaptive selector
 * (`selectAdaptiveQuestionV11` keeps families whose `tier` is in the group's
 * allowed range), so a "filled" tier here is exactly a tier the selector can
 * draw a question from for this module.
 */
function getFilledTiersForModule(moduleId: ModuleId, group: GroupId): number[] {
  const range = getTierRangeForGroup(group);
  const tiers = new Set<number>();
  for (const family of getFamiliesForModule(moduleId)) {
    if (family.tier >= range.min && family.tier <= range.max) {
      tiers.add(family.tier);
    }
  }
  return [...tiers].sort((a, b) => a - b);
}

/**
 * Snap a desired starting/placement tier to the nearest tier (within the
 * group) that actually has content for the module.
 *
 * Why this exists: the date-based starting tier (and the manual B/M/E tier)
 * is content-blind — it always points at the period's non-plus tier for the
 * group, regardless of whether THIS module has families there. For
 * content-sparse modules this can land a fresh learner on an empty band
 * (e.g. GELD in groep 6 has no families at E6/E6+ — tiers 5–6 — so the
 * date-derived "Eind Groep 6" placement points at nothing, even though the
 * module is fully usable at tiers 1–4).
 *
 * Behaviour:
 * - If the desired tier already has content, it is returned unchanged.
 * - Otherwise we snap to the nearest filled tier AT OR BELOW the desired tier
 *   (never push a learner past authored content).
 * - If nothing exists at or below, we snap UP to the lowest filled tier.
 * - If the module has no content in the group at all, the desired tier is
 *   returned unchanged (nothing better to do; the selector's own fallbacks
 *   handle the empty case).
 */
export function clampTierToModuleContent(desiredTier: number, group: GroupId, moduleId: ModuleId): number {
  const filled = getFilledTiersForModule(moduleId, group);
  if (filled.length === 0) return desiredTier;
  if (filled.includes(desiredTier)) return desiredTier;
  const atOrBelow = filled.filter(t => t <= desiredTier);
  if (atOrBelow.length > 0) return atOrBelow[atOrBelow.length - 1];
  return filled[0];
}

/**
 * Get the automatic starting tier for a group based on the current date.
 * Returns the tier of the first level (non-plus) for the resolved phase.
 *
 * When `moduleId` is supplied, the result is clamped to the nearest tier that
 * actually has content for that module (see `clampTierToModuleContent`). Omit
 * `moduleId` for module-agnostic placement (e.g. inferred-mastery seeding).
 */
export function getAutoStartingTier(group: GroupId, date: Date = new Date(), moduleId?: ModuleId): number {
  const phase = resolvePhaseByDate(date);

  // Find the tier matching group + phase + !isPlus
  const tierConfig = TIERS.find(t => t.group === group && t.period === phase && !t.isPlus);
  let tier: number;
  if (tierConfig) {
    tier = tierConfig.tier;
  } else {
    // Fallback: the first tier of the group
    const firstTier = TIERS.find(t => t.group === group);
    if (firstTier) {
      tier = firstTier.tier;
    } else {
      // Defensive last-resort fallback — only reachable if TIERS data is
      // corrupt (no entry exists for `group` at all). The previous ladder
      // (`group===6?1:group===7?7:13`) duplicated tier-bandgrenzen as magic
      // numbers; we instead surface the corruption via a warning so it can
      // be diagnosed rather than silently returning an incorrect tier.
      // — R6-5 fix, mei 2026.
      warn(`[placementEngine] No tier found for group ${group} — TIERS data corrupt? Falling back to tier 1.`);
      tier = 1;
    }
  }

  // Content-aware clamp: don't strand a fresh learner on a tier the module
  // has no content for (e.g. GELD groep 6 at E6/E6+).
  if (moduleId) {
    tier = clampTierToModuleContent(tier, group, moduleId);
  }
  return tier;
}

/**
 * Get a human-readable label for the current auto-detected phase.
 */
export function getAutoPhaseLabel(date: Date = new Date()): string {
  const phase = resolvePhaseByDate(date);
  switch (phase) {
    case 'B':
      return 'Begin schooljaar';
    case 'M':
      return 'Midden schooljaar';
    case 'E':
      return 'Eind schooljaar';
  }
}